import {
  ProfessionalAdminProfile as ApiProfessionalAdminProfile,
  ProfessionalPrivateProfile as ApiProfessionalPrivateProfile,
  type CreateProfessionalBody,
  type GetProfessionalActivitiesStatusFilter,
  type GetProfessionalsAdminStatusFilter,
  ImproveProfessionalBioBodyLocale,
  ProfessionalActivity,
  ProfessionalPublicProfile,
  UpdateProfessionalBody
} from '@api/models';

import { ApiClient } from '@website/lib/api';
import { browserFetch } from '@website/lib/api/browserFetch';
import { getAssetUrl } from '@website/utils/assets';
import { calculateSessionsSinceLastYear } from '@website/utils/date';
import { getTranslation } from '@website/utils/i18n';

import type { CacheInfo } from './activities.service';

/**
 * Fetch the timestamp of the most recent static-content invalidation for this
 * professional. The backend returns `{ cacheInvalidatedAt: null }` to
 * non-owners (rather than 403), so the call is safe to make speculatively
 * from the profile page.
 */
export const getProfessionalCacheInfo = async (professionalId: string): Promise<CacheInfo> => {
  try {
    return await browserFetch<CacheInfo>(`/professionals/${professionalId}/cache-info`, { method: 'GET' });
  } catch (error) {
    console.error('Failed to fetch professional cache info:', error);
    return { cacheInvalidatedAt: null };
  }
};

/**
 * Normalize phone number to E.164 format by removing spaces.
 * E.164 format: +[country code][number] with no spaces.
 * Example: "+234 801 234 5678" -> "+2348012345678"
 */
function normalizePhoneNumber(phoneNumber?: string): string | undefined {
  if (!phoneNumber) return undefined;
  return phoneNumber.replace(/\s+/g, '');
}

export interface GetProfessionalsParams {
  locale?: string;
  query?: string;
  cursor?: string;
  limit?: number;
}

export interface GetProfessionalsAdminParams extends GetProfessionalsParams {
  statusFilter?: GetProfessionalsAdminStatusFilter;
  /**
   * When `statusFilter='pending'` and this is true, the backend includes
   * `incomplete` professionals in the result set (mentors still working on
   * their profile). Default false so admins only see review-ready profiles.
   */
  includeIncomplete?: boolean;
  /**
   * Filter results by group membership. Pass an array of group IDs (any-match)
   * or include the sentinel `__none__` to also match professionals with no
   * groups. Empty array = no filter.
   * Backed by `users.groups` (single source of truth — see option C in the
   * groups feature design).
   */
  groupIds?: string[];
}

/** Sentinel for "no groups" passed alongside real group IDs to the backend. */
export const NO_GROUPS_TOKEN = '__none__';

// ---------------------------------------------------------------------------
// Professional Revisions (shared shape used on every view)
// ---------------------------------------------------------------------------

export interface ProfessionalRevision {
  id: string;
  professionalId: string;
  submittedBy: string;
  status: 'pending' | 'approved' | 'declined';
  data: {
    phoneNumber?: string;
    publicEmail?: string;
    profilePictureKey?: string;
    verificationDocumentKey?: string;
    bio?: { en?: string; fr?: string; es?: string };
    shortBio?: { en?: string; fr?: string; es?: string };
    socialMediaLinks?: {
      website?: string;
      instagram?: string;
      facebook?: string;
      twitter?: string;
      linkedin?: string;
    };
    fullName?: string;
    address?: string;
  };
  rejectionReason?: string;
  rejectionSections?: string[];
  reviewedBy?: string;
  reviewedAt?: number;
  createdAt: number;
  updatedAt: number;
}

// ---------------------------------------------------------------------------
// Shared frontend view-models
// ---------------------------------------------------------------------------

export type ProfessionalStatus = 'incomplete' | 'pending' | 'approved' | 'rejected';

export interface Professional {
  id: string;
  name: string;
  address?: string;
  profileImageUrl: string | null;
  label: string;
  joinedYear: number;
  sessionsCount: number;
  activityCount: number;
  bio?: string;
  email?: string;
  phoneNumber?: string;
  verificationDocumentKey?: string | null;
  status?: ProfessionalStatus;
  socials?: {
    instagram?: string;
    facebook?: string;
    twitter?: string;
    website?: string;
    linkedin?: string;
  };
}

/**
 * Owner+admin view of a professional, returned by `getProfessionalPrivateProfile`.
 *
 * This is the source of truth the owner (or an admin) uses to drive UI that
 * depends on moderation state (pending/approved/rejected) and to surface the
 * list of revisions for the declined-feedback banner, the "revision is
 * pending" banner, and the sidebar status badge.
 */
export interface ProfessionalPrivateProfile extends Professional {
  revisions: ProfessionalRevision[];
  hasPendingRevision: boolean;
  latestRevision: ProfessionalRevision | null;
  /** Admin-supplied reason from the last decline (preserved across incomplete → pending). */
  rejectionReason?: string;
  /** Top-level sections the admin flagged on the last decline. */
  rejectionSections?: string[];
  /** Dot-path identifiers for flagged sub-fields (e.g. `personalInfo.mobileNumber`). */
  rejectionFields?: string[];
  /** Sections the mentor has NOT yet meaningfully changed since the decline. */
  pendingSections?: string[];
  /** Fields the mentor has NOT yet meaningfully changed since the decline. */
  pendingFields?: string[];
}

export interface ProfessionalAdmin extends ProfessionalPrivateProfile {
  userId: string;
  privateEmail?: string;
  isProfessional?: boolean;
  isAdmin?: boolean;
  accountCreationDate?: number;
  stripeOnboardingComplete?: boolean;
  blockedAt?: number;
  blockReason?: string | null;
  /** Owner's user.groups — admin-managed audience tags. */
  groups?: string[];
}

// ---------------------------------------------------------------------------
// Revision-management mutations
// ---------------------------------------------------------------------------

/** Admin: approve the pending revision for a professional. */
export const approveProfessionalRevision = async (professionalId: string): Promise<void> => {
  await ApiClient.approveProfessionalRevision(professionalId, {});
};

/** Admin: decline the pending revision for a professional with a reason. */
export const declineProfessionalRevision = async (
  professionalId: string,
  rejectionReason: string,
  rejectionSections?: string[]
): Promise<void> => {
  await ApiClient.declineProfessionalRevision(professionalId, {
    rejectionReason,
    rejectionSections
  });
};

export async function createProfessional(params: Omit<CreateProfessionalBody, 'userId'> & { userId?: string }) {
  const body: CreateProfessionalBody = params.userId ? (params as CreateProfessionalBody) : (params as any);
  return ApiClient.createProfessional(body as any);
}

// ---------------------------------------------------------------------------
// Mappers
// ---------------------------------------------------------------------------

function transformProfessional(
  professional: ProfessionalPublicProfile & {
    activityCount?: number;
    status?: ProfessionalStatus;
  },
  locale: string = 'en'
): Professional {
  const bioObj: { [key: string]: string | undefined } = {};
  if (professional.bio) {
    if (typeof professional.bio === 'string') {
      bioObj.en = professional.bio;
    } else if (typeof professional.bio === 'object') {
      Object.entries(professional.bio).forEach(([key, value]) => {
        bioObj[key] = value as string;
      });
    }
  }

  const translatedBio = getTranslation(bioObj, locale, '');
  const profileImageUrl = professional.profilePictureKey ? getAssetUrl(professional.profilePictureKey) : null;

  return {
    id: professional.id,
    name: `${professional.firstName || ''} ${professional.lastName || ''}`.trim() || professional.fullName || 'Unknown',
    address: professional.address,
    profileImageUrl,
    label: 'Professional',
    joinedYear: new Date(professional.joinedDate).getFullYear(),
    sessionsCount: professional.totalSessionsGiven,
    activityCount: professional.activityCount || 0,
    bio: translatedBio,
    socials: professional.socialMediaLinks,
    email: professional.publicEmail,
    phoneNumber: normalizePhoneNumber(professional.phoneNumber),
    status: professional.status
  };
}

function transformProfessionalPrivateProfile(
  profile: ApiProfessionalPrivateProfile,
  locale: string
): ProfessionalPrivateProfile {
  const base = transformProfessional(profile as unknown as ProfessionalPublicProfile, locale);
  const revisions = ((profile.revisions ?? []) as unknown as ProfessionalRevision[]) || [];
  // Revisions come back newest first, so the first element is the latest.
  const latestRevision = revisions[0] ?? null;
  const hasPendingRevision = revisions.some((r) => r.status === 'pending');

  const raw = profile as unknown as {
    rejectionReason?: string;
    rejectionSections?: string[];
    rejectionFields?: string[];
    pendingSections?: string[];
    pendingFields?: string[];
  };

  return {
    ...base,
    verificationDocumentKey: profile.verificationDocumentKey || null,
    revisions,
    hasPendingRevision,
    latestRevision,
    rejectionReason: raw.rejectionReason,
    rejectionSections: raw.rejectionSections,
    rejectionFields: raw.rejectionFields,
    pendingSections: raw.pendingSections,
    pendingFields: raw.pendingFields
  };
}

function transformProfessionalAdminProfile(profile: ApiProfessionalAdminProfile, locale: string): ProfessionalAdmin {
  const base = transformProfessionalPrivateProfile(profile as unknown as ApiProfessionalPrivateProfile, locale);
  return {
    ...base,
    userId: profile.userId || profile.id,
    privateEmail: profile.email,
    isProfessional: profile.isProfessional,
    isAdmin: profile.isAdmin,
    accountCreationDate: profile.accountCreationDate,
    stripeOnboardingComplete: profile.stripeOnboardingComplete,
    blockedAt: profile.blockedAt,
    blockReason: (profile as { blockReason?: string | null }).blockReason ?? null,
    groups: (profile as { groups?: string[] }).groups ?? []
  };
}

// Status transformers accept whatever subset the server returns. `transformProfessional`
// typed `status` narrowly before the incomplete rollout; relax it at the edges so UI
// callers can see the real value.

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export async function getProfessionals(
  { locale = 'en', query, cursor, limit }: GetProfessionalsParams = {},
  options?: RequestInit
): Promise<{
  professionals: Professional[];
  nextCursor?: string;
}> {
  try {
    const response = await ApiClient.getProfessionals(
      {
        query,
        cursor,
        limit
      },
      options
    );

    const professionalsData = Array.isArray(response) ? response : (response as any).data || [response];
    const nextCursor = (response as any).cursor;

    const professionals = professionalsData.map(
      (
        professional: ProfessionalPublicProfile & {
          activityCount?: number;
          status?: ProfessionalStatus;
        }
      ) => transformProfessional(professional, locale)
    );

    return {
      professionals,
      nextCursor
    };
  } catch (error) {
    console.error('Error fetching professionals:', error);
    throw error;
  }
}

export async function getProfessional(id: string, locale: string = 'en'): Promise<Professional | null> {
  try {
    const professionalData = await ApiClient.getProfessionalPublicProfile(id);

    if (!professionalData) {
      return null;
    }

    return transformProfessional(professionalData, locale);
  } catch (error) {
    console.error(`Error fetching professional with ID ${id}:`, error);
    return null;
  }
}

/**
 * Owner+admin: fetch a professional's own profile together with status and
 * revision history. Any authenticated user may call this for their own id,
 * and admins can call it for anyone.
 */
export async function getProfessionalPrivateProfile(
  id: string,
  locale: string = 'en'
): Promise<ProfessionalPrivateProfile | null> {
  try {
    const profile = await ApiClient.getProfessionalPrivateProfile(id);
    if (!profile) return null;
    return transformProfessionalPrivateProfile(profile, locale);
  } catch (error) {
    console.error(`Error fetching professional private profile with ID ${id}:`, error);
    return null;
  }
}

/**
 * Admin-only: fetch a professional profile with sensitive user fields.
 * Superset of {@link getProfessionalPrivateProfile}.
 */
export async function getProfessionalAdminProfile(
  id: string,
  locale: string = 'en'
): Promise<ProfessionalAdmin | null> {
  try {
    const profile = await ApiClient.getProfessionalAdminProfile(id);
    if (!profile) return null;
    return transformProfessionalAdminProfile(profile, locale);
  } catch (error) {
    console.error(`Error fetching admin professional profile with ID ${id}:`, error);
    return null;
  }
}

export async function getProfessionalsAdmin(
  { locale = 'en', query, cursor, limit, statusFilter, includeIncomplete, groupIds }: GetProfessionalsAdminParams = {},
  options?: RequestInit
): Promise<{
  professionals: ProfessionalAdmin[];
  nextCursor?: string;
}> {
  try {
    const groupId = groupIds && groupIds.length > 0 ? groupIds.join(',') : undefined;
    const response = await ApiClient.getProfessionalsAdmin(
      {
        query,
        statusFilter,
        cursor,
        limit,
        ...(includeIncomplete ? { includeIncomplete: true } : {}),
        ...(groupId ? { groupId } : {})
      } as any,
      options
    );

    const professionalsData = Array.isArray(response) ? response : (response as any).data || [response];
    const nextCursor = (response as any).cursor;

    // The batch endpoint doesn't return a full revision array for each row — it
    // only surfaces a `hasPendingRevision` boolean. Callers that need the full
    // revision history must call `getProfessionalPrivateProfile` or
    // `getProfessionalAdminProfile` for a specific id.
    const professionals = professionalsData.map((professionalData: any): ProfessionalAdmin => {
      const bioObj: { [key: string]: string | undefined } = {};
      if (professionalData.bio) {
        if (typeof professionalData.bio === 'string') {
          bioObj.en = professionalData.bio;
        } else if (typeof professionalData.bio === 'object') {
          Object.entries(professionalData.bio).forEach(([key, value]) => {
            bioObj[key] = value as string;
          });
        }
      }

      const translatedBio = getTranslation(bioObj, locale, '');
      const profileImageUrl = professionalData.profilePictureKey
        ? getAssetUrl(professionalData.profilePictureKey)
        : null;

      const hasPendingRevision = !!professionalData.hasPendingRevision;

      return {
        id: professionalData.id,
        name:
          `${professionalData.firstName || ''} ${professionalData.lastName || ''}`.trim() ||
          professionalData.fullName ||
          'Unknown',
        address: professionalData.address,
        profileImageUrl,
        label: 'Professional',
        joinedYear: new Date(professionalData.joinedDate).getFullYear(),
        sessionsCount: professionalData.totalSessionsGiven,
        activityCount: professionalData.activityCount || 0,
        bio: translatedBio,
        socials: professionalData.socialMediaLinks,
        email: professionalData.publicEmail,
        phoneNumber: normalizePhoneNumber(professionalData.phoneNumber),
        verificationDocumentKey: professionalData.verificationDocumentKey || null,
        userId: professionalData.userId,
        privateEmail: professionalData.email,
        isProfessional: professionalData.isProfessional,
        isAdmin: professionalData.isAdmin,
        accountCreationDate: professionalData.accountCreationDate,
        status: professionalData.status,
        stripeOnboardingComplete: professionalData.stripeOnboardingComplete,
        blockedAt: professionalData.blockedAt,
        blockReason: (professionalData as { blockReason?: string | null }).blockReason ?? null,
        groups: professionalData.groups ?? [],
        rejectionReason: professionalData.rejectionReason,
        rejectionSections: professionalData.rejectionSections,
        rejectionFields: professionalData.rejectionFields,
        revisions: [],
        hasPendingRevision,
        latestRevision: null
      };
    });

    return {
      professionals,
      nextCursor
    };
  } catch (error) {
    console.error('Error fetching admin professionals:', error);
    throw error;
  }
}

export async function updateProfessional(id: string, updates: UpdateProfessionalBody): Promise<any> {
  return ApiClient.updateProfessional(id, updates);
}

export async function getProfessionalActivities(
  professionalId: string,
  locale: string = 'en',
  statusFilter?: GetProfessionalActivitiesStatusFilter
): Promise<any[]> {
  try {
    // Fetch ALL pages of activities. Backend defaults to a small page size (10)
    // and exposes max 100 per page, so we loop on the cursor until exhausted.
    const PAGE_LIMIT = 100;
    const activitiesData: ProfessionalActivity[] = [];
    let cursor: string | undefined = undefined;

    do {
      const apiParams: any = { limit: PAGE_LIMIT };
      if (statusFilter) apiParams.statusFilter = statusFilter;
      if (cursor) apiParams.cursor = cursor;

      const apiActivitiesResponse = await ApiClient.getProfessionalActivities(professionalId, apiParams);

      if (!apiActivitiesResponse || typeof apiActivitiesResponse !== 'object') {
        break;
      }

      const pageData = Array.isArray(apiActivitiesResponse)
        ? apiActivitiesResponse
        : (apiActivitiesResponse as any).data || [apiActivitiesResponse];

      activitiesData.push(...pageData);
      cursor = (apiActivitiesResponse as any).cursor || undefined;
    } while (cursor);

    return activitiesData.map((activity: ProfessionalActivity) => {
      const titleObj: { [key: string]: string | undefined } = {};
      if (activity.title) {
        if (typeof activity.title === 'string') {
          titleObj.en = activity.title;
        } else if (typeof activity.title === 'object') {
          Object.entries(activity.title).forEach(([key, value]) => {
            titleObj[key] = value as string;
          });
        }
      }

      const translatedTitle = getTranslation(titleObj, locale, 'Untitled Activity');
      const descriptionObj: { [key: string]: string | undefined } = {};
      if (activity.description) {
        if (typeof activity.description === 'string') {
          descriptionObj.en = activity.description;
        } else if (typeof activity.description === 'object') {
          Object.entries(activity.description).forEach(([key, value]) => {
            descriptionObj[key] = value as string;
          });
        }
      }
      const translatedDescription = getTranslation(descriptionObj, locale, 'No description available');
      const imageUrl = getAssetUrl(activity.mainImageKey);

      const firstName = activity.professionalName?.firstName || '';
      const lastName = activity.professionalName?.lastName || '';
      const professionalName = [firstName, lastName].filter(Boolean).join(' ') || 'Unknown Professional';

      return {
        id: activity.id,
        title: translatedTitle,
        description: translatedDescription,
        imageUrl,
        professionalName,
        professionalId: activity.professionalId,
        isAtHome: activity.isAtHome === null ? undefined : activity.isAtHome,
        startingPrice: activity.startingPrice || 0,
        completedSessionsCount: activity.completedSessionsCount || 0,
        sessionsSinceLastYear: activity.sessions ? calculateSessionsSinceLastYear(activity.sessions) : 0,
        location: {
          address: activity.locationAddress || 'Location not specified',
          coordinates: {
            latitude: activity.latitude || 0,
            longitude: activity.longitude || 0
          }
        },
        category: activity.categoryId,
        ratingAverage: activity.ratingAverage ?? 0,
        ratingTotal: activity.ratingTotal ?? 0
      };
    });
  } catch (error) {
    console.error(`Error fetching activities for professional ${professionalId}:`, error);
    return [];
  }
}

export interface ImproveProfessionalBioParams {
  bio: string;
  locale?: string;
  professionalName?: string;
  specialty?: string;
}

export interface ImproveProfessionalBioResult {
  bio: string;
}

/** Send the current bio to the AI and receive an improved version. */
export const improveProfessionalBio = async (
  params: ImproveProfessionalBioParams
): Promise<ImproveProfessionalBioResult> => {
  return ApiClient.improveProfessionalBio({
    bio: params.bio,
    // useLocale() returns a wide string; the API only accepts en/fr/es.
    // Send only when it's one of the supported values, otherwise omit and
    // let the AI auto-detect the input language.
    locale: isSupportedLocale(params.locale) ? (params.locale as ImproveProfessionalBioBodyLocale) : undefined,
    professionalName: params.professionalName,
    specialty: params.specialty
  });
};

function isSupportedLocale(locale: string | undefined): boolean {
  return locale === 'en' || locale === 'fr' || locale === 'es';
}
